import React, { useRef } from 'react';
import logoImg from '../../logo.png';

export default function InvoiceModal({ order, onClose, showPrint = true }) {
  const printRef = useRef(null);
  const formatPrice = (p) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(p);
  const fmtDate = (d) => new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const win = window.open('', '_blank', 'width=800,height=900');
    win.document.write(`
      <html><head><title>Invoice ${order.order_number}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 40px; max-width: 700px; margin: auto; color: #1a1a1a; }
        .header { text-align: center; border-bottom: 2px solid #0d9488; padding-bottom: 16px; margin-bottom: 24px; }
        .header img { height: 50px; margin-bottom: 8px; }
        .header h1 { font-size: 22px; margin: 4px 0; color: #0d9488; }
        .header p { font-size: 12px; color: #666; margin: 2px 0; }
        .order-number { text-align: right; font-size: 14px; font-weight: bold; margin-bottom: 16px; }
        .info { display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 13px; }
        .info strong { display: block; margin-bottom: 4px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th { background: #f9fafb; padding: 8px 12px; text-align: left; font-size: 11px; text-transform: uppercase; color: #666; border-bottom: 1px solid #e5e7eb; }
        td { padding: 8px 12px; font-size: 13px; border-bottom: 1px solid #f3f4f6; }
        .text-right { text-align: right; }
        .summary { margin-left: auto; width: 280px; font-size: 13px; }
        .summary div { display: flex; justify-content: space-between; padding: 4px 0; }
        .summary .total { font-size: 18px; font-weight: bold; border-top: 2px solid #1a1a1a; padding-top: 8px; margin-top: 8px; }
        .footer { text-align: center; font-size: 11px; color: #999; margin-top: 40px; border-top: 1px solid #e5e7eb; padding-top: 16px; }
        @media print { body { padding: 20px; } .no-print { display: none; } }
      </style></head>
      <body>
        <div style="text-align:right;margin-bottom:16px" class="no-print">
          <button onclick="window.print()" style="padding:8px 24px;background:#0d9488;color:white;border:none;border-radius:6px;cursor:pointer;font-size:14px">🖨️ Cetak</button>
        </div>
        ${content}
      </body></html>
    `);
    win.document.close();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center rounded-t-2xl z-10">
          <h2 className="text-lg font-bold text-gray-800">Invoice / Tagihan</h2>
          <div className="flex gap-2">
            {showPrint && <button onClick={handlePrint} className="px-4 py-2 bg-brand text-white rounded-lg text-sm hover:bg-brand-dark">🖨️ Cetak</button>}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
          </div>
        </div>

        <div ref={printRef} className="p-6">
          {/* Header */}
          <div className="text-center border-b-2 border-brand pb-4 mb-6">
            <img src={logoImg} alt="Klinnovac" className="h-12 mx-auto mb-2" />
            <h1 className="text-xl font-bold text-navy">Klinnovac</h1>
            <p className="text-sm text-gray-500">Inovasi untuk Hidup Lebih Bersih dan Sehat</p>
            <p className="text-xs text-gray-400">Jl. Inovasi Bersih No. 123, Jakarta Selatan | +62 21 1234 5678</p>
          </div>

          <div className="text-right mb-4">
            <p className="text-xs text-gray-400">INVOICE</p>
            <p className="text-lg font-bold font-mono text-gray-800">{order.order_number}</p>
            <p className="text-xs text-gray-400">{fmtDate(order.created_at)}</p>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
            <div>
              <strong className="text-gray-700 block mb-1">Pelanggan</strong>
              <p className="font-medium">{order.customer_name}</p>
              <p className="text-gray-500">{order.customer_phone}</p>
              {order.customer_email && <p className="text-gray-500">{order.customer_email}</p>}
              {order.customer_address && <p className="text-gray-500 text-xs mt-1">{order.customer_address}</p>}
            </div>
            <div className="text-right">
              <strong className="text-gray-700 block mb-1">Status</strong>
              <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${order.status === 'completed' ? 'bg-green-100 text-green-700' : order.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{order.status}</span>
              <p className="text-xs text-gray-400 mt-2">Metode: <span className="capitalize font-medium">{order.payment_method}</span></p>
              <p className="text-xs text-gray-400">Pembayaran: <span className="capitalize font-medium">{order.payment_status}</span></p>
            </div>
          </div>

          <table className="w-full text-sm mb-6">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-2 text-xs text-gray-500 uppercase">Item</th>
                <th className="text-center py-2 text-xs text-gray-500 uppercase">Qty</th>
                <th className="text-right py-2 text-xs text-gray-500 uppercase">Harga</th>
                <th className="text-right py-2 text-xs text-gray-500 uppercase">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {(order.items || []).map((item, idx) => (
                <tr key={idx} className="border-b border-gray-100">
                  <td className="py-2">{item.name}</td>
                  <td className="py-2 text-center">{item.quantity}</td>
                  <td className="py-2 text-right">{formatPrice(item.unit_price)}</td>
                  <td className="py-2 text-right font-medium">{formatPrice(item.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="ml-auto w-72 text-sm">
            <div className="flex justify-between py-1"><span className="text-gray-500">Subtotal</span><span>{formatPrice(order.subtotal)}</span></div>
            <div className="flex justify-between py-1"><span className="text-gray-500">Pajak</span><span>{formatPrice(order.tax_amount)}</span></div>
            {order.discount_amount > 0 && <div className="flex justify-between py-1"><span className="text-gray-500">Diskon</span><span className="text-red-500">- {formatPrice(order.discount_amount)}</span></div>}
            <div className="flex justify-between py-2 text-lg font-bold border-t-2 border-gray-800 mt-2"><span>Total</span><span>{formatPrice(order.total)}</span></div>
          </div>

          {order.notes && (
            <div className="mt-6 bg-gray-50 p-3 rounded-lg text-sm">
              <span className="font-medium text-gray-600">Catatan:</span> {order.notes}
            </div>
          )}

          <div className="text-center text-xs text-gray-400 mt-8 pt-4 border-t">
            Terima kasih telah menggunakan layanan Klinnovac
          </div>
        </div>
      </div>
    </div>
  );
}